import { addToRecord, objectWithoutKeys, updateRecord } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  FileUploadJobCanceledAction,
  FileUploadJobCompleteSessionStartedAction,
  FileUploadJobCompletedAction,
  FileUploadJobFailedAction,
  FileUploadJobProgressChangedAction,
  FileUploadJobRemovedAction,
  FileUploadJobSessionCreatedAction,
  FileUploadJobStartedAction,
  UploadToAzureCompletedAction,
  UploadToSharePointCompletedAction,
} from '../../domains/fileUploadJobs/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
  PublisherAttachmentsRemovedAction,
} from '../../domains/publishers/actions.types';
import { Publisher } from '../types';

import { FileUploadJobs, UploadStatus } from './types';

export type FileUploadJobAction =
  | FileUploadJobCompletedAction
  | FileUploadJobCompleteSessionStartedAction
  | FileUploadJobStartedAction
  | FileUploadJobSessionCreatedAction
  | FileUploadJobProgressChangedAction
  | FileUploadJobCanceledAction
  | FileUploadJobFailedAction
  | FileUploadJobRemovedAction
  | UploadToAzureCompletedAction
  | UploadToSharePointCompletedAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublisherAttachmentsRemovedAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction;

const initialState: FileUploadJobs = {};

const startUpload = (
  state: FileUploadJobs,
  { id, fileName, fileSize, publisherId }: FileUploadJobStartedAction['payload']
) =>
  addToRecord(state, id, {
    id,
    fileName,
    fileSize,
    bytesUploaded: 0,
    status: UploadStatus.Started,
    publisherId,
  });

const sessionCreated = (state: FileUploadJobs, { id, uploadSession }: FileUploadJobSessionCreatedAction['payload']) =>
  updateRecord(state, id, { uploadSession, status: UploadStatus.SessionCreated });

const updateProgress = (state: FileUploadJobs, { id, bytesUploaded }: FileUploadJobProgressChangedAction['payload']) =>
  updateRecord(state, id, { bytesUploaded });

const completeSessionStarted = (state: FileUploadJobs, { id }: FileUploadJobCompleteSessionStartedAction['payload']) =>
  updateRecord(state, id, { status: UploadStatus.CompleteSessionStarted });

const completeUpload = (
  state: FileUploadJobs,
  { id, result, smallFileUploadStorageProvider }: FileUploadJobCompletedAction['payload']
) => {
  if (!state[id]) {
    return state;
  }

  return updateRecord(state, id, {
    bytesUploaded: state[id].fileSize,
    status: UploadStatus.Completed,
    schema: result.schema,
    fileId: result.id,
    smallFileUploadStorageProvider,
  });
};

const uploadToAzureCompleted = (state: FileUploadJobs, { id }: UploadToAzureCompletedAction['payload']) =>
  updateRecord(state, id, { status: UploadStatus.UploadToStorageCompleted });

const uploadToSharePointCompleted = (
  state: FileUploadJobs,
  { id, sharePointFileId }: UploadToSharePointCompletedAction['payload']
) => updateRecord(state, id, { sharePointFileId, status: UploadStatus.UploadToStorageCompleted });

const cancelUpload = (state: FileUploadJobs, { id }: FileUploadJobCanceledAction['payload']) =>
  updateRecord(state, id, { status: UploadStatus.Canceled });

const abandonAllPublisherUploads = (state: FileUploadJobs, payload: PublisherAttachmentsRemovedAction['payload']) => {
  const { id: publisherId, attachmentReferences } = payload;
  const attachmentReferenceIds = attachmentReferences.map((reference) => reference.id);

  return Object.keys(state).reduce((updatedState, jobId) => {
    if (attachmentReferenceIds.includes(jobId) && state[jobId].publisherId === publisherId) {
      return updateRecord(updatedState, jobId, { abandoned: true });
    }

    return updatedState;
  }, state);
};

const failedUpload = (state: FileUploadJobs, { id, errorCode, errorTimestamp }: FileUploadJobFailedAction['payload']) =>
  updateRecord(state, id, { status: UploadStatus.Failed, errorCode, errorTimestamp });

const clearJobsForPublisher = (state: FileUploadJobs, postedFromPublisherId: Publisher['id']) => {
  const toRemoveKeys = Object.keys(state).filter((key) => state[key].publisherId === postedFromPublisherId);
  if (toRemoveKeys.length === 0) {
    return state;
  }

  return objectWithoutKeys(state, toRemoveKeys);
};

const reducer: Reducer<FileUploadJobs, FileUploadJobAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FILE_UPLOAD_JOB.STARTED':
      return startUpload(state, action.payload);
    case 'FILE_UPLOAD_JOB.COMPLETE_SESSION_STARTED':
      return completeSessionStarted(state, action.payload);
    case 'FILE_UPLOAD_JOB.PROGRESS_CHANGED':
      return updateProgress(state, action.payload);
    case 'FILE_UPLOAD_JOB.UPLOAD_TO_AZURE_COMPLETED':
      return uploadToAzureCompleted(state, action.payload);
    case 'FILE_UPLOAD_JOB.UPLOAD_TO_SHAREPOINT_COMPLETED':
      return uploadToSharePointCompleted(state, action.payload);
    case 'FILE_UPLOAD_JOB.COMPLETED':
      return completeUpload(state, action.payload);
    case 'FILE_UPLOAD_JOB.CANCELED':
      return cancelUpload(state, action.payload);
    case 'FILE_UPLOAD_JOB.FAILED':
      return failedUpload(state, action.payload);
    case 'FILE_UPLOAD_JOB.SESSION_CREATED':
      return sessionCreated(state, action.payload);
    case 'FILE_UPLOAD_JOB.REMOVED':
      return objectWithoutKeys(state, [action.payload.id]);
    case 'PUBLISHER.ATTACHMENTS_REMOVED':
      return abandonAllPublisherUploads(state, action.payload);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return clearJobsForPublisher(state, action.payload.publisherId);
    default:
      return state;
  }
};

export default reducer;
